import Dexie from "dexie";
import type { PersistedUserAppData } from "../../app/persistence/userAppData.types";

export type StoredUserSound = {
	id: number;
	name: string;
	blob: Blob;
	mimeType: string;
	size: number;
};

export type StoredStrudelSound = {
	id: number;
	name: string;
	pattern: string;
	bpm: number;
	bars: number;
	blob: Blob;
	mimeType: string;
	size: number;
};

const dexie = new Dexie("piano-boom", {
	autoOpen: false,
});

function dataUrlToBlob(dataUrl: string): Blob {
	const colonIdx = dataUrl.indexOf(":");
	const semicolonIdx = dataUrl.indexOf(";");
	const commaIdx = dataUrl.indexOf(",");
	const mimeType = dataUrl.slice(colonIdx + 1, semicolonIdx);
	const base64 = dataUrl.slice(commaIdx + 1);
	const binaryStr = atob(base64);
	const bytes = new Uint8Array(binaryStr.length);
	for (let i = 0; i < binaryStr.length; i++) {
		bytes[i] = binaryStr.charCodeAt(i);
	}
	return new Blob([bytes], { type: mimeType });
}

export { dataUrlToBlob };

const V1_STORES = {
	userSounds: "id++,name,mimeType,size",
	userAppData: "id++",
} as const;

dexie.version(2).stores(V1_STORES);
dexie.version(3).stores({
	...V1_STORES,
	strudelSounds: "id++,name,bpm,bars",
});

const typedDb = dexie as Dexie & {
	userSounds: Dexie.Table<StoredUserSound, number, Omit<StoredUserSound, "id">>;
	userAppData: Dexie.Table<
		PersistedUserAppData,
		number,
		Omit<PersistedUserAppData, "id">
	>;
	strudelSounds: Dexie.Table<
		StoredStrudelSound,
		number,
		Omit<StoredStrudelSound, "id">
	>;
};

export async function openDatabaseConnection() {
	if (dexie.isOpen()) {
		return dexie;
	}
	return await dexie.open();
}

export const DB = {
	queries: {
		async getStoredUserAppData() {
			if (!typedDb.isOpen()) {
				throw new Error("Database not initialized");
			}
			if (!typedDb.userAppData) {
				throw new Error("Database not initialized");
			}

			return (await typedDb.userAppData.toArray())[0] ?? null;
		},
		getAllUserSounds() {
			if (!typedDb.isOpen()) {
				throw new Error("Database not initialized");
			}
			if (!typedDb.userSounds) {
				throw new Error("Database not initialized");
			}

			return typedDb.userSounds.toArray();
		},
		getUserSoundCount() {
			return typedDb.userSounds.count();
		},
		getUserSoundPage(params: {
			start: number;
			count: number;
			search?: string;
		}) {
			const { count, start, search } = params;

			if (search) {
				return typedDb.userSounds
					.filter((sound) => sound.name.toLowerCase().includes(search))
					.offset(start)
					.limit(count)
					.toArray();
			}
			return typedDb.userSounds.offset(start).limit(count).toArray();
		},
		getUserSoundById(id: number) {
			return typedDb.userSounds.get(id);
		},
	},
	mutations: {
		async setStoredUserAppData(data: Omit<PersistedUserAppData, "id">) {
			if (!typedDb.isOpen()) {
				throw new Error("Database not initialized");
			}
			if (!typedDb.userAppData) {
				throw new Error("Database not initialized");
			}

			return await typedDb.userAppData.put(data, 1);
		},
		async addUserSound(sound: Omit<StoredUserSound, "id">) {
			if (!typedDb.isOpen()) {
				throw new Error("Database not initialized");
			}
			if (!typedDb.userSounds) {
				throw new Error("Database not initialized");
			}

			return await typedDb.userSounds.add(sound);
		},
		async updateUserSound(updateData: StoredUserSound) {
			if (!typedDb.isOpen()) {
				throw new Error("Database not initialized");
			}
			if (!typedDb.userSounds) {
				throw new Error("Database not initialized");
			}

			const existingSound = await typedDb.userSounds.get(updateData.id);
			if (!existingSound) {
				return null;
			}
			const updateResult = await typedDb.userSounds.put(updateData);
			return {
				...updateData,
				id: updateResult,
			};
		},
		async deleteUserSound(id: number) {
			if (!typedDb.isOpen()) {
				throw new Error("Database not initialized");
			}
			if (!typedDb.userSounds) {
				throw new Error("Database not initialized");
			}

			const existingSound = await typedDb.userSounds.get(id);
			if (!existingSound) {
				return null;
			}

			await typedDb.userSounds.delete(id);
			return existingSound;
		},
		async addStrudelSound(sound: Omit<StoredStrudelSound, "id">) {
			if (!typedDb.isOpen()) {
				throw new Error("Database not initialized");
			}
			return await typedDb.strudelSounds.add(sound);
		},
		async updateStrudelSound(sound: StoredStrudelSound) {
			if (!typedDb.isOpen()) {
				throw new Error("Database not initialized");
			}
			const existing = await typedDb.strudelSounds.get(sound.id);
			if (!existing) return null;
			await typedDb.strudelSounds.put(sound);
			return sound;
		},
		// Persists only the pattern source/metadata, leaving the rendered clip
		// (blob/mimeType/size) untouched — used by "Save & Exit" so an edited but
		// un-rerendered pattern is kept without invalidating assigned Keys.
		async updateStrudelSoundMeta(
			id: number,
			meta: Pick<StoredStrudelSound, "name" | "pattern" | "bpm" | "bars">,
		) {
			if (!typedDb.isOpen()) {
				throw new Error("Database not initialized");
			}
			const existing = await typedDb.strudelSounds.get(id);
			if (!existing) return null;
			const updated: StoredStrudelSound = { ...existing, ...meta, id };
			await typedDb.strudelSounds.put(updated);
			return updated;
		},
		async deleteStrudelSound(id: number) {
			if (!typedDb.isOpen()) {
				throw new Error("Database not initialized");
			}
			const existing = await typedDb.strudelSounds.get(id);
			if (!existing) return null;
			await typedDb.strudelSounds.delete(id);
			return existing;
		},
	},
	strudelQueries: {
		getAllStrudelSounds() {
			if (!typedDb.isOpen()) {
				throw new Error("Database not initialized");
			}
			return typedDb.strudelSounds.toArray();
		},
		getStrudelSoundById(id: number) {
			return typedDb.strudelSounds.get(id);
		},
		getStrudelSoundCount() {
			return typedDb.strudelSounds.count();
		},
	},
} as const;
